import { useState } from "react";
import { useRail } from "../../../shared/phone/media";
import { PhoneNav } from "../../../shared/phone/PhoneNav";
import { Segmented } from "../../../shared/phone/Row";
import { Sheet } from "../../../shared/phone/Sheet";
import { Split } from "../../../shared/phone/Split";
import { Icon } from "../../components/ui/Icon";
import type { RunLine } from "../../lib/ccApi";
import type { GoalView } from "../../lib/goalsApi";
import { FILTERS, ICON, PAGE, runName, runWhen, useLog, type Kind, type Row } from "./LogPage";
import { plural } from "./text";

const TONE: Record<Kind, string> = { run: "", think: "good", call: "plain", guard: "violet", note: "plain", err: "danger" };
const show = (v: unknown) => (typeof v === "string" ? v : JSON.stringify(v, null, 2));

/** One step of a run: its icon, what it did and when; a step with details opens in place to what went in and came back. */
function Step({ e }: { e: Row }) {
  const [open, setOpen] = useState(false);
  const det = e.i !== undefined || e.o !== undefined;
  const body = (
    <>
      <span className={TONE[e.k] ? `pn-ico ${TONE[e.k]}` : "pn-ico"}>
        <Icon name={ICON[e.k]} />
      </span>
      <span className="m">
        <span className="gp-evt">{e.x}</span>
        {open && (
          <span className="gp-evd">
            {e.i !== undefined && (
              <span>
                <b>What went in</b>
                <code>{show(e.i)}</code>
              </span>
            )}
            {e.o !== undefined && (
              <span>
                <b>What came back</b>
                <code>{show(e.o)}</code>
              </span>
            )}
          </span>
        )}
      </span>
      <span className="gp-evr">
        <span className="gp-evtm">{e.t}</span>
        {det && <Icon name="chev" />}
      </span>
    </>
  );
  return det ? (
    <button type="button" className={`pn-row gp-ev ${e.k}${open ? " open" : ""}`} aria-expanded={open} onClick={() => setOpen((o) => !o)}>
      {body}
    </button>
  ) : (
    <div className={`pn-row static gp-ev ${e.k}`}>{body}</div>
  );
}

/** The runs as rows, the one shown ticked. */
function RunRows({ runs, on, more, onPick, onOlder }: { runs: RunLine[]; on: string; more: boolean; onPick: (id: string) => void; onOlder: () => void }) {
  return (
    <div className="pn-grp">
      {runs.map((r) => (
        <button type="button" key={r.id} className={`pn-row${r.id === on ? " on" : ""}`} aria-current={r.id === on ? "true" : undefined} onClick={() => onPick(r.id)}>
          <span className="m">
            <span className="t">{runName(r)}</span>
            <span className="s">{runWhen(r)}</span>
          </span>
          {r.id === on && (
            <span className="gp-tick">
              <Icon name="check" />
            </span>
          )}
        </button>
      ))}
      {more && (
        <button type="button" className="pn-row" onClick={onOlder}>
          <span className="m">
            <span className="t gp-acc">Older runs</span>
            <span className="s">Show {PAGE} more</span>
          </span>
        </button>
      )}
    </div>
  );
}

/**
 * Everything the agent did, on a phone and an iPad (the prototype's log): pick a run from the row at the top, or
 * from the list beside it on an iPad; All, Thinking, Steps and Rules; each step opens in place.
 */
export function LogNative({ view }: { view: GoalView }) {
  const g = view.goal;
  const goalPath = `/goals/${encodeURIComponent(g.id)}`;
  const rail = useRail();
  const { runs, more, older, run, events, error, filter, setFilter, q, setQ, shown, n, download, pick } = useLog(view);
  const [picking, setPicking] = useState(false);

  // Why: on a phone the run goes to Files, Mail or Messages through the phone's own share; elsewhere it downloads.
  const share = async () => {
    if (!run) return;
    const file = new File([JSON.stringify({ run, events: events[run.id] ?? [] }, null, 2)], `run-${run.id}.json`, { type: "application/json" });
    if (navigator.canShare?.({ files: [file] })) {
      try {
        await navigator.share({ files: [file], title: `${runName(run)}, ${runWhen(run)}` });
      } catch {
        // The person closed the share sheet.
      }
    } else download();
  };
  const shareBtn = (
    <button type="button" className="pn-tbtn" onClick={() => void share()} aria-label="Share this run" disabled={!run}>
      <Icon name="share" />
    </button>
  );

  const steps = run && (
    <div className="pn-stack tight gp-steps">
      <p className="gp-logsum">
        {plural(n("call") + n("guard"), "step")} · {plural(n("think"), "thought")} · {plural(n("guard"), "rule check")}
      </p>
      <Segmented value={filter} options={FILTERS.map(([value, label]) => ({ value, label }))} onChange={setFilter} label="Show" />
      <label className="pn-search">
        <Icon name="search" />
        <input type="search" placeholder="Search the log" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search the log" />
      </label>
      <div className="pn-grp gp-evs">
        {shown.length ? (
          shown.map((e) => <Step key={e.key} e={e} />)
        ) : (
          <div className="gp-ev0">{events[run.id] ? "Nothing here for this run." : "Loading…"}</div>
        )}
      </div>
      <p className="pn-note">Every step is written down as it happens. Nobody can edit the log, not even the agent.</p>
    </div>
  );

  const empty = !runs ? <p className="pn-note">{error ?? "Loading the log…"}</p> : <div className="pn-empty">No tasks for this goal yet.</div>;

  if (rail)
    return (
      <Split
        listWidth={320}
        showDetail
        list={
          <div className="pn-screen gp-log">
            <PhoneNav title="Everything the agent did" large back={{ to: goalPath, label: "Goal" }} />
            <div className="pn-stack tight">
              {runs && run ? (
                <section>
                  <div className="pn-cap">Runs</div>
                  <RunRows runs={runs} on={run.id} more={more} onPick={pick} onOlder={older} />
                </section>
              ) : (
                empty
              )}
            </div>
          </div>
        }
        detail={
          <div className="pn-screen gp-log gp-log-ip">
            <PhoneNav title="" actions={shareBtn} />
            {run && (
              <div className="gp-runh">
                <h1>{runName(run)}</h1>
                <span>{runWhen(run)}</span>
              </div>
            )}
            {steps}
          </div>
        }
      />
    );

  return (
    <div className="pn-screen gp-log">
      <PhoneNav title="Everything the agent did" large back={{ to: goalPath, label: "Goal" }} actions={shareBtn} />
      {runs && run ? (
        <div className="pn-stack tight gp-log-ph">
          <div className="pn-grp">
            <button type="button" className="pn-row gp-runr" onClick={() => setPicking(true)} aria-haspopup="dialog">
              <span className="pn-ico">
                <Icon name="play" />
              </span>
              <span className="m">
                <span className="t">{runName(run)}</span>
                <span className="s">{runWhen(run)}</span>
              </span>
              <span className="v">{runs.length === 1 ? "1 run" : `${runs.length}${more ? "+" : ""} runs`}</span>
              <Icon name="chev" />
            </button>
          </div>
          {steps}
        </div>
      ) : (
        empty
      )}
      {runs && run && (
        <Sheet open={picking} onClose={() => setPicking(false)} title="Runs" tall>
          <RunRows
            runs={runs}
            on={run.id}
            more={more}
            onPick={(id) => {
              pick(id);
              setPicking(false);
            }}
            onOlder={older}
          />
        </Sheet>
      )}
    </div>
  );
}
